'use client';

import { useQueries } from 'convex/react';
import type { FunctionReference, FunctionReturnType } from 'convex/server';
import type { Value } from 'convex/values';
import { useMemo, useState } from 'react';

import type { api } from '@/lib/convex';

type EntityQuery = typeof api.vfs.public.listDirectoriesByEntityPaginated;
type EntityPage = FunctionReturnType<EntityQuery>;
type EntityRow = EntityPage['page'][number];

export const ENTITY_DIRECTORY_PAGE_SIZE = 50;
/** Bounds a search walk to ENTITY_DIRECTORY_MAX_PAGES * page size rows. */
export const ENTITY_DIRECTORY_MAX_PAGES = 10;
const FIRST_PAGE: ReadonlyArray<string | null> = [null];

/**
 * Entity-indexed directories (brands, products, ...) for a picker search.
 * Loads the first page, and while `walk` is set follows `continueCursor` up
 * to a bounded number of pages so a typed term can match a later page.
 * A failed page ends the walk with the rows loaded so far; nothing throws.
 */
export function useEntityDirectoryPages(
  query: EntityQuery,
  args: { orgSlug: string; entity: string } | 'skip',
  walk: boolean
): { rows: EntityRow[]; isPending: boolean } {
  // `api.module.fn` builds a new reference on every access; keep the first.
  const [reference] = useState(() => query);
  const argsKey = args === 'skip' ? null : JSON.stringify(args);
  const [walkState, setWalkState] = useState<{
    key: string | null;
    cursors: ReadonlyArray<string | null>;
  }>({ key: argsKey, cursors: FIRST_PAGE });
  // Clearing the search (or new args) drops back to the first page only, so
  // later-page subscriptions end and the next search walks afresh.
  const cursors =
    walk && walkState.key === argsKey ? walkState.cursors : FIRST_PAGE;
  if (!walk && walkState.cursors !== FIRST_PAGE) {
    setWalkState({ key: argsKey, cursors: FIRST_PAGE });
  }

  const requests = useMemo(() => {
    const request: Record<
      string,
      { query: FunctionReference<'query'>; args: Record<string, Value> }
    > = {};
    if (argsKey === null) return request;
    cursors.forEach((cursor, index) => {
      request[`page${index}`] = {
        query: reference,
        args: {
          ...JSON.parse(argsKey),
          paginationOpts: { numItems: ENTITY_DIRECTORY_PAGE_SIZE, cursor },
        },
      };
    });
    return request;
  }, [argsKey, cursors, reference]);
  const results = useQueries(requests);

  const { rows, next, isPending } = useMemo(() => {
    const loadedRows: EntityRow[] = [];
    let nextCursor: string | null = null;
    let pending = argsKey !== null;
    for (let index = 0; index < cursors.length; index++) {
      const page: unknown = results[`page${index}`];
      if (page === undefined) break;
      if (index === 0) pending = false;
      if (page instanceof Error) break;
      const loaded = page as EntityPage;
      loadedRows.push(...loaded.page);
      if (index === cursors.length - 1 && !loaded.isDone) {
        nextCursor = loaded.continueCursor;
      }
    }
    return { rows: loadedRows, next: nextCursor, isPending: pending };
  }, [argsKey, cursors, results]);
  if (
    walk &&
    next !== null &&
    cursors.length < ENTITY_DIRECTORY_MAX_PAGES &&
    !cursors.includes(next)
  ) {
    // Render-time update from the previous page's result (React's
    // "storing information from previous renders" pattern).
    setWalkState({ key: argsKey, cursors: [...cursors, next] });
  }
  return { rows, isPending };
}
